/**
 * ============================================
 * PS5 ANALYTICS HOOK
 * ============================================
 * 
 * React hook for fetching PlayStation 5 analytics data.
 * Handles caching status, refresh, and error states.
 */

import { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '@/config';

// ============================================
// TYPES
// ============================================

export interface PS5Summary {
  totalUsers: number;
  totalUniqueUsers?: number;
  totalDownloads?: number;
  totalInstalls?: number;
  allTimeUsers?: number;
  currentEngaged?: number;
  currentEngagedMonth?: string;
  latestDAU: number;
  peakDAU?: number;
  highestDAU?: number;
  currentMAU: number;
  totalSessions: number;
  totalHours: number;
  totalPlaytimeHours?: number;
  avgSessionMinutes: number;
  totalCountries: number;
  overallCrashRate: number;
  crashRate?: number;
  currentCrashRate?: number;
  currentCrashDate?: string;
  dataDate: string;
  // PS Plus session-type rollup for the latest month present in the PS Plus
  // dataset. NOTE: session type, NOT membership (Sony's Off/On Plus labels do
  // not indicate who holds a PS Plus subscription).
  psPlusMonth?: string;
  psPlusOffMau?: number;
  psPlusOnMau?: number;
}

export interface PS5PsPlusSegment {
  mau: number;
  sessions: number;
  hours: number;
}

export interface PS5PsPlusMonth {
  Month: string;
  MonthStartDate: string;
  offPlus: PS5PsPlusSegment;
  streaming: PS5PsPlusSegment;
  total: PS5PsPlusSegment;
}

export interface PS5DailyStat {
  Date: string;
  DAU: number;
  Devices: number;
  TotalSessions: number;
  TotalHours: number;
  AvgSessionMinutes: number;
}

export interface PS5MonthlyStat {
  Month: string;
  MonthStartDate: string;
  MAU: number;
  TotalSessions: number;
  TotalHours: number;
  AvgSessionMinutes: number;
}

export interface PS5GeoEntry {
  Country: string;
  Region: string;
  Users: number;
  Sessions: number;
  Hours: number;
  Percentage: number;
  AvgSessionMinutes: number;
}

export interface PS5CrashDaily {
  Date: string;
  TotalSessions: number;
  TotalCrashes: number;
  DevicesWithCrashes: number;
  TotalDevices: number;
  CrashRate: number;
}

export interface PS5CrashByVersion {
  Version: string;
  TotalSessions: number;
  TotalCrashes: number;
  CrashRate: number;
}

export interface PS5CrashByRegion {
  Region: string;
  TotalSessions: number;
  TotalCrashes: number;
  CrashRate: number;
}

export interface PS5CrashStats {
  daily: PS5CrashDaily[];
  byVersion: PS5CrashByVersion[];
  byRegion: PS5CrashByRegion[];
}

export interface PS5RetentionEntry {
  month: string;
  monthStart: string;
  acquired: number;
  retained: number;
  reclaimed: number;
  lapsed: number;
  active: number;
  acquiredLTD: number;
  lapsedLTD: number;
}

export interface PS5InstallData {
  firstInstalls: number;
  reinstalls: number;
  totalInstalls: number;
  totalUninstalls: number;
}

export interface PS5AnalyticsData {
  summary: PS5Summary;
  dailyStats: PS5DailyStat[];
  // Old, unfiltered (Concept+Platform only) daily DAU — overcounts ~4-8x; used
  // only by the separate "old/unfiltered DAU" card.
  dailyStatsUnfiltered?: PS5DailyStat[];
  monthlyStats: PS5MonthlyStat[];
  geoBreakdown: PS5GeoEntry[];
  crashStats: PS5CrashStats;
  retentionData?: PS5RetentionEntry[];
  installData?: PS5InstallData;
  psPlusBreakdown?: PS5PsPlusMonth[];
  error?: string;
  configured?: boolean;
}

export interface PS5CacheStatus {
  configured: boolean;
  cached: boolean;
  lastFetch: string | null;
  cacheAgeHours: number | null;
  isValid: boolean;
}

// ============================================
// HELPER
// ============================================

async function fetchPS5API<T>(endpoint: string): Promise<T> {
  const url = `${BACKEND_URL}${endpoint}`;
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  return response.json();
}

// ============================================
// HOOK
// ============================================

export function usePS5Analytics() {
  const [data, setData] = useState<PS5AnalyticsData | null>(null);
  const [cacheStatus, setCacheStatus] = useState<PS5CacheStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch analytics data
  const fetchData = useCallback(async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const url = forceRefresh 
        ? '/api/v1/ps5/analytics?refresh=true'
        : '/api/v1/ps5/analytics';
      
      const response = await fetchPS5API<PS5AnalyticsData>(url);
      setData(response);

      // Also fetch cache status
      const statusResponse = await fetchPS5API<PS5CacheStatus>('/api/v1/ps5/status');
      setCacheStatus(statusResponse);

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch PS5 analytics';
      setError(message);
      console.error('PS5 Analytics fetch error:', err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Force refresh
  const refresh = useCallback(() => {
    return fetchData(true);
  }, [fetchData]);

  // Export PDF
  const exportPDF = useCallback(async (countries: string[] = []) => {
    try {
      const qs = countries.length ? `?countries=${encodeURIComponent(countries.join(','))}` : '';
      const response = await fetch(`${BACKEND_URL}/api/v1/ps5/export/pdf${qs}`);
      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lokko_ps5_report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('PDF export error:', err);
      throw err;
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    cacheStatus,
    isLoading,
    isRefreshing,
    error,
    refresh,
    exportPDF,
    isConfigured: cacheStatus?.configured ?? false,
  };
}
